import React, { useState } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import JsonViewerTab from './JsonViewerTab';
import './DebugTabs.css';

/**
 * Tab panel wrapper component
 */
const TabPanel = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`debug-tabpanel-${index}`}
    aria-labelledby={`debug-tab-${index}`}
  >
    {value === index && (
      <Box className="debug-tabs__panel">
        {children}
      </Box>
    )}
  </div>
);

/**
 * Tab configuration
 */
const TAB_CONFIG = [
  { id: 'case', label: 'Case' },
  { id: 'patient', label: 'Patient' },
  { id: 'provider', label: 'Provider' },
  { id: 'plan', label: 'Plan' },
  { id: 'planInstance', label: 'Plan Instance' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'interactions', label: 'Interactions' },
];

/**
 * DebugTabs - Tabbed interface for viewing case debug information
 *
 * Displays JSON data across 7 tabs:
 * - Case, Patient, Provider, Plan, Plan Instance, Schedule, Interactions
 */
const DebugTabs = ({
  // Data from useCaseDebugInfo hook
  caseData = {},
  patientData = {},
  providerData = {},
  organizationData = {},
  organizationGroupData = {},
  outcomePlanData = {},
  taskGraphTemplatesData = {},
  outcomePlanInstanceData = {},
  taskGraphInstancesData = {},
  executionsData = {},
  nextExecutionTimeData = {},
  schedulerStatusData = {},
  interactionRequestsData = {},
  interactionResponsesData = {},
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <div className="debug-tabs">
      <Box className="debug-tabs__header">
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Debug information tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              minWidth: 'auto',
              padding: '12px 16px',
            },
            '& .Mui-selected': {
              color: '#22223B',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#22223B',
            },
          }}
        >
          {TAB_CONFIG.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              id={`debug-tab-${index}`}
              aria-controls={`debug-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Case Tab */}
      <TabPanel value={selectedTab} index={0}>
        <JsonViewerTab title="Case" data={caseData} />
      </TabPanel>

      {/* Patient Tab */}
      <TabPanel value={selectedTab} index={1}>
        <JsonViewerTab title="Patient" data={patientData} />
      </TabPanel>

      {/* Provider Tab */}
      <TabPanel value={selectedTab} index={2}>
        <JsonViewerTab title="Provider" data={providerData} />
        <JsonViewerTab title="Organization" data={organizationData} />
        <JsonViewerTab title="Group" data={organizationGroupData} />
      </TabPanel>

      {/* Plan Tab */}
      <TabPanel value={selectedTab} index={3}>
        <JsonViewerTab title="Plan" data={outcomePlanData} />
        <JsonViewerTab title="Task Graph Templates" data={taskGraphTemplatesData} />
      </TabPanel>

      {/* Plan Instance Tab */}
      <TabPanel value={selectedTab} index={4}>
        <JsonViewerTab title="Plan Instance" data={outcomePlanInstanceData} />
        <JsonViewerTab title="Task Graph Instances" data={taskGraphInstancesData} />
        <JsonViewerTab title="Executions" data={executionsData} />
      </TabPanel>

      {/* Schedule Tab */}
      <TabPanel value={selectedTab} index={5}>
        <JsonViewerTab title="Next Execution Time" data={nextExecutionTimeData} />
        <JsonViewerTab title="Scheduler Status" data={schedulerStatusData} />
      </TabPanel>

      {/* Interactions Tab */}
      <TabPanel value={selectedTab} index={6}>
        <JsonViewerTab title="Interaction Requests" data={interactionRequestsData} />
        <JsonViewerTab title="Interaction Responses" data={interactionResponsesData} />
      </TabPanel>
    </div>
  );
};

export default DebugTabs;
